import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, Menu, X, Truck } from "lucide-react";
import Logo from "./Logo";
import { useStore } from "../context/StoreContext";

const NAV = [
  { label: "Начало", to: "/" },
  { label: "Картини", to: "/kartini" },
  { label: "Колекции", to: "/kartini?view=collections" },
  { label: "За дома", to: "/kartini?room=Дневна" },
  { label: "Подаръци", to: "/podaratsi" },
  { label: "Портрети", to: "/portreti" },
  { label: "За художника", to: "/za-avtora" },
  { label: "Контакти", to: "/kontakti" },
];

export default function Header() {
  const { cartCount, wishlist } = useStore();
  const [open, setOpen] = useState(false);
  const [showSearch, setShowSearch] = useState(false);
  const [query, setQuery] = useState("");
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    if (query.trim()) { navigate(`/kartini?search=${encodeURIComponent(query.trim())}`); setShowSearch(false); setQuery(""); }
  };

  return (
    <header className={`sticky top-0 z-50 bg-ivory/95 backdrop-blur transition-shadow ${scrolled ? "shadow-[0_1px_0_rgba(198,164,92,0.4)]" : ""}`}
            data-testid="site-header">
      <div className="bg-navy text-ivory text-center text-[12px] sm:text-[13px] tracking-wide py-2 px-4 flex items-center justify-center gap-2" data-testid="free-shipping-bar">
        <Truck size={15} className="text-gold shrink-0" />
        <span><b className="text-gold">Безплатна доставка</b> за всички поръчки над 99 €</span>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-[72px]">
          {/* Left: mobile hamburger + logo */}
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-navy" onClick={() => setOpen(true)} data-testid="mobile-menu-open" aria-label="Меню">
              <Menu size={24} />
            </button>
            <Logo />
          </div>

          {/* Center nav */}
          <nav className="hidden lg:flex items-center gap-7">
            {NAV.map((n) => (
              <Link key={n.label} to={n.to}
                    className="text-[13px] font-medium uppercase tracking-wide text-navy hover:text-gold transition-colors"
                    data-testid={`nav-${n.label}`}>
                {n.label}
              </Link>
            ))}
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-3 sm:gap-4">
            <button onClick={() => setShowSearch((s) => !s)} className="text-navy hover:text-gold transition-colors" data-testid="search-toggle" aria-label="Търсене">
              <Search size={20} />
            </button>
            <Link to="/lyubimi" className="relative text-navy hover:text-gold transition-colors hidden sm:block" data-testid="wishlist-link" aria-label="Любими">
              <Heart size={20} />
              {wishlist.length > 0 && <span className="absolute -top-2 -right-2 bg-gold text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full">{wishlist.length}</span>}
            </Link>
            <Link to="/kolichka" className="relative text-navy hover:text-gold transition-colors" data-testid="cart-link" aria-label="Количка">
              <ShoppingBag size={20} />
              {cartCount > 0 && <span className="absolute -top-2 -right-2 bg-gold text-white text-[10px] w-4 h-4 flex items-center justify-center rounded-full" data-testid="cart-count">{cartCount}</span>}
            </Link>
            <Link to="/kartini" className="hidden md:inline-flex btn-gold px-5 py-2.5 text-sm rounded-sm" data-testid="header-cta">
              Разгледай картините
            </Link>
          </div>
        </div>
      </div>
      <div className="gold-rule" />

      {/* Search bar */}
      {showSearch && (
        <div className="border-b border-stone bg-ivory" data-testid="search-bar">
          <form onSubmit={submitSearch} className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
            <Search size={18} className="text-gold" />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                   placeholder="Търсете картина по име..."
                   className="flex-1 bg-transparent outline-none text-navy placeholder:text-charcoal/40"
                   data-testid="search-input" />
            <button type="submit" className="btn-gold px-4 py-1.5 text-sm rounded-sm" data-testid="search-submit">Търси</button>
          </form>
        </div>
      )}

      {/* Mobile menu */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="mobile-menu">
          <div className="absolute inset-0 bg-navy/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-[82%] max-w-sm bg-ivory p-6 flex flex-col">
            <div className="flex items-center justify-between mb-8">
              <Logo />
              <button onClick={() => setOpen(false)} className="text-navy" data-testid="mobile-menu-close"><X size={24} /></button>
            </div>
            <nav className="flex flex-col gap-1">
              {NAV.map((n) => (
                <Link key={n.label} to={n.to} onClick={() => setOpen(false)}
                      className="py-3 border-b border-stone text-navy font-medium text-lg font-serif"
                      data-testid={`mobile-nav-${n.label}`}>
                  {n.label}
                </Link>
              ))}
            </nav>
            <Link to="/kartini" onClick={() => setOpen(false)} className="btn-gold text-center py-3 mt-8 rounded-sm">
              Разгледай картините
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
